/**
 * Task #1950 + #1951 — Tipi condivisi per il modello SIP extensions
 * (frontend modale + backend Zod + sync agent Kamailio).
 *
 * Source of truth runtime backend: `packages/backend/src/schemas/sip-extension.schema.ts`.
 * Questo file espone interface + costanti + validatori CIDR client-side
 * (no dipendenze backend).
 *
 * Sezioni:
 * - X2 (#1950): tipi UI modale Extensions (outbound class, recording mode,
 *   security profile, allowlist editor entry, validatori CIDR client-side).
 * - X3 (#1951): tipi runtime enforcement allowlist su Kamailio (snapshot
 *   esposto dall'endpoint interno LAN `GET /api/internal/kamailio/extension-allowlist`,
 *   consumato dal sync agent per popolare `ht_ext_allow` su KAM-1).
 */

// ─── X2 (#1950) — UI modale Extensions ───────────────────────────────────────

export const OUTBOUND_CLASSES = [
  'internal_only',
  'internal_national',
  'national_mobile',
  'international',
  'premium',
] as const;
export type OutboundClass = (typeof OUTBOUND_CLASSES)[number];

export const RECORDING_MODES = ['always', 'on_demand', 'disabled'] as const;
export type RecordingMode = (typeof RECORDING_MODES)[number];

export const SECURITY_PROFILES = ['webrtc', 'sip_secure', 'sip_legacy'] as const;
export type SecurityProfile = (typeof SECURITY_PROFILES)[number];

export interface ExtensionAllowedIpEntry {
  cidr: string;
  label: string;
}

/**
 * Validatore client-side CIDR IPv4/IPv6. Allinea il comportamento a
 * `backend/src/utils/cidr.ts::isValidCidr` (lazy: regex + bound prefix).
 */
const IPV4_OCTET = '(25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]?\\d)';
const IPV4_RE = new RegExp(`^${IPV4_OCTET}(\\.${IPV4_OCTET}){3}(/(3[0-2]|[12]?\\d))?$`);
// IPv6 conservativo: accetta gruppi esadecimali separati da `:` con eventuale
// `::` compresso. Non valida tutte le forme RFC (es. mappature IPv4) ma copre
// l'uso operativo (allowlist firewall per-extension).
const IPV6_RE = /^(([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|::([0-9a-fA-F]{1,4}:){0,6}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|::)(\/(12[0-8]|1[01]\d|[1-9]?\d))?$/;

export function isValidCidrClient(input: string): boolean {
  if (typeof input !== 'string') return false;
  const v = input.trim();
  if (!v) return false;
  return IPV4_RE.test(v) || IPV6_RE.test(v);
}

export function detectCidrKind(input: string): 'v4' | 'v6' | 'invalid' {
  const v = (input || '').trim();
  if (IPV4_RE.test(v)) return 'v4';
  if (IPV6_RE.test(v)) return 'v6';
  return 'invalid';
}

// ─── X3 (#1951) — Runtime enforcement Kamailio ───────────────────────────────

/**
 * Riga del snapshot esposto dall'endpoint interno LAN
 * `GET /api/internal/kamailio/extension-allowlist`, consumata dal sync
 * agent per popolare la htable `ht_ext_allow` su KAM-1.
 *
 * Conteniamo SOLO i 3 campi che servono al runtime SBC: niente label, niente
 * metadata UI. La grammatica CIDR è già validata a write-time da
 * `packages/backend/src/schemas/sip-extension.schema.ts` (allowedIpEntrySchema
 * con `isValidCidr`), quindi qui le stringhe sono assunte well-formed.
 */
export interface ExtensionAllowlistEntry {
  readonly tenant_id: string;
  readonly extension_number: string;
  readonly cidrs: readonly string[];
}
